import { useEffect, useState, useMemo, useCallback } from 'react';
import { FormControl, InputLabel, MenuItem, Select, Chip, Tooltip, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { ReportingAPI } from '../../../api/Reporting';
import Loader from '../../../component/Loader';
import { PrivilegeAPI } from '../../../api/PrivilegesAPI';
import { Privileges } from '../../../util/PrivilegeConstants';
import { EventAPI } from '../../../api/EventAPI';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import defaultProfileImg from '../../../../src/assets/images/default_profile_img.png';
import { FaRegEye, FaFileExport } from 'react-icons/fa';
import { isClubOwner, isClubStaff } from '../../../util/UserRoles';
import { AiFillDelete } from 'react-icons/ai';
import CustomAlertDialog from '../../CustomComponents/CustomAlertDialog';
import { useDispatch } from 'react-redux';
import { rootAction } from '../../../redux/reducer/rootReducer';

// ✅ Custom Toolbar with Quick Filter
const CustomToolbar = ({ onExportClick, buttonRef, quickFilterProps, exportDisabled }) => {
    return (
        <div style={{
            width: '100%',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            padding: '8px 16px',
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <div style={{ flex: 1 }}>
                    <GridToolbarQuickFilter
                        {...quickFilterProps}
                        sx={{
                            '& .MuiInputBase-root': {
                                borderRadius: '8px',
                                height: '38px',
                                backgroundColor: 'white',
                                width: '320px',
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '14px',
                                padding: '0 12px',
                            },
                        }}
                        placeholder="Search by Player Name or Email..."
                        debounceMs={400}
                        quickFilterParser={(searchInput) =>
                            searchInput.split(',').map((value) => value.trim())
                        }
                    />
                </div>
                <div>
                    <Tooltip title="Export Report">
                        <span>
                            <Button
                                ref={buttonRef}
                                variant="contained"
                                size="small"
                                onClick={onExportClick}
                                startIcon={<FaFileExport />}
                                disabled={exportDisabled}
                                sx={{
                                    backgroundColor: 'var(--hoverColorBold)',
                                    '&:hover': {
                                        backgroundColor: 'var(--hoverColorBold)',
                                        opacity: 0.9,
                                    },
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderRadius: '8px',
                                    padding: '4px 16px',
                                    fontSize: '13px',
                                    pointerEvents: 'auto',
                                    '&.Mui-disabled': {
                                        backgroundColor: '#cccccc',
                                    },
                                }}
                            >
                                Export Report
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

const Roster = () => {
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const [players, setPlayers] = useState([]);
    const [events, setEvents] = useState([]);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [openAlert, setOpenAlert] = useState({ isOpen: false, player: {} });
    const [privileges, setPrivileges] = useState({});
    const [filterData, setFilterData] = useState({});
    const [loadingState, setLoadingState] = useState({
        loading1: false,
        loading2: false,
        loading3: false,
        loading4: false
    });
    // ✅ Pagination model
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [totalPlayers, setTotalPlayers] = useState(0);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [searchText, setSearchText] = useState('');

    // ✅ Track sorting state with default sort by firstName
    const [sortModel, setSortModel] = useState([
        { field: 'firstName', sort: 'asc' }
    ]);

    const navigator = useNavigate();
    const dispatch = useDispatch();

    const startLoading = (key) => {
        setLoadingState((prev) => ({
            ...prev, [key]: true
        }))
    };
    const finishLoading = (key) => {
        setLoadingState((prev) => ({
            ...prev, [key]: false
        }))
    };

    const handleDeleteEventPlayerRegistration = async () => {
        try {
            startLoading("loading4");
            const reqObj = {
                playerId: openAlert.player.playerId,
                clubId: selectedClub.clubId,
                eventId: currentEvent?.eventId
            }
            await EventAPI.postDeleteEventRegistration(reqObj);
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'success', content: `${openAlert.player?.firstName} ${openAlert.player?.lastName}'s registration has been deleted. The user can now re-register from the start.` }));
            setPlayers((prev) => prev.filter(p => p.playerId != openAlert.player.playerId));
            setOpenAlert({ isOpen: false, player: {} })
        } catch (error) {
            console.error(error);
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: 'Please contact customer support' }));
        } finally {
            finishLoading("loading4");
            setOpenAlert(false);
        }
    };

    const findAllPrivileges = async () => {
        try {
            startLoading('loading1');
            const reqObj = {
                userId: userInfo.userId,
                featureName: Object.keys(Privileges)[13]
            }
            const response = await PrivilegeAPI.getAllPrivilegesByFeature(reqObj);
            setPrivileges(response.Data);
        } catch (error) {
            console.error(error);
        } finally {
            finishLoading("loading1")
        }
    };

    // ✅ Fixed fetchPlayers with proper validation and sorting support
    const fetchPlayers = async (filterText = '', size, pageNum, sortData = null) => {
        // ✅ Validate required parameters but don't early return silently
        if (!selectedClub?.clubId) {
            console.warn('Club ID is missing, cannot fetch players');
            setPlayers([]);
            setTotalPlayers(0);
            return;
        }
        
        // ✅ If no event ID, just return empty data without calling API
        if (!currentEvent?.eventId) {
            console.warn('Event ID is missing, cannot fetch players');
            setPlayers([]);
            setTotalPlayers(0);
            return;
        }

        try {
            const finalSize = size !== undefined ? size : paginationModel.pageSize;
            const finalPage = pageNum !== undefined ? pageNum : paginationModel.page;

            // ✅ Build sort params - preserve sorting
            let sortParams = {};
            if (sortData) {
                // Use the provided sort data (from sorting change)
                sortParams = sortData;
            } else if (sortModel && sortModel.length > 0) {
                // Use the current sort model
                const sort = sortModel[0];
                const fieldMap = {
                    'firstName': 'firstName',
                    'lastName': 'lastName',
                    'email': 'email',
                    'clubName': 'clubName',
                    'mobileNumber': 'mobileNumber'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            startLoading('loading4');
            const response = await EventAPI.getAllEventPlayer(
                selectedClub.clubId,
                currentEvent.eventId,
                finalSize,
                finalPage,
                filterText || '',
                filterData,
                sortParams
            );
            setPlayers(response.Data || []);
            setTotalPlayers(response.Data?.length > 0 ? response.Data[0].totalPlayer : 0);
        } catch (error) {
            console.error('Error fetching players:', error);
            setPlayers([]);
            setTotalPlayers(0);
        } finally {
            finishLoading('loading4');
        }
    };

    // ✅ Fixed handleSortModelChange with proper validation
    const handleSortModelChange = useCallback((newSortModel) => {
        setSortModel(newSortModel);
        const filterValue = filterModel?.quickFilterValues?.join(',') || '';
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        // ✅ Only fetch if we have both club and event IDs
        if (!selectedClub?.clubId || !currentEvent?.eventId) {
            console.warn('Cannot sort: missing club or event ID');
            return;
        }
        
        let sortParams = {};
        if (newSortModel && newSortModel.length > 0) {
            const sort = newSortModel[0];
            const fieldMap = {
                'firstName': 'firstName',
                'lastName': 'lastName',
                'email': 'email',
                'clubName': 'clubName',
                'mobileNumber': 'mobileNumber'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        // ✅ Call fetchPlayers with the sort parameters
        fetchPlayers(filterValue, paginationModel.pageSize, 0, sortParams);
    }, [paginationModel.pageSize, filterModel, selectedClub?.clubId, currentEvent?.eventId]);

    const findAllEventOptionByClub = async () => {
        try {
            startLoading('loading2');
            const response = await EventAPI.getAllEventForListOptions(selectedClub.clubId);
            if (response.Data && response.Data.length > 0) {
                setCurrentEvent(response.Data[0]);
                const filterValue = filterModel?.quickFilterValues?.join(',') || '';
                // ✅ Sort is preserved here by passing null (uses current sortModel)
                await fetchPlayers(filterValue, paginationModel.pageSize, 0);
            } else {
                setEvents([]);
                setCurrentEvent(null);
                setPlayers([]);
                setTotalPlayers(0);
            }
            setEvents(response.Data || []);
        } catch (error) {
            console.error('Error fetching events:', error);
            setEvents([]);
            setCurrentEvent(null);
        } finally {
            finishLoading('loading2');
        }
    };

    useEffect(() => {
        if (userInfo.userId) {
            findAllEventOptionByClub();
            findAllPrivileges();
        }
    }, [userInfo.userId]);

    // ✅ Fixed handleFilterChange with proper validation
    const handleFilterChange = useCallback((newFilterModel) => {
        setFilterModel(newFilterModel);
        const quickFilterValue = newFilterModel?.quickFilterValues?.join(',') || '';
        setSearchText(quickFilterValue);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        // ✅ Only fetch if we have both IDs
        if (!selectedClub?.clubId || !currentEvent?.eventId) {
            return;
        }
        
        let sortParams = {};
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            const fieldMap = {
                'firstName': 'firstName',
                'lastName': 'lastName',
                'email': 'email',
                'clubName': 'clubName',
                'mobileNumber': 'mobileNumber'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchPlayers(quickFilterValue, paginationModel.pageSize, 0, sortParams);
    }, [paginationModel.pageSize, sortModel, selectedClub?.clubId, currentEvent?.eventId]);

    // ✅ Fixed handleEventChange with proper validation
    const handleEventChange = async (event) => {
        try {
            const eventId = event.target.value;
            const selectedEvent = events.find(e => e.eventId === eventId);
            setCurrentEvent(selectedEvent);
            setPaginationModel(prev => ({ ...prev, page: 0 }));
            setFilterModel({ items: [] });
            setSearchText('');
            // Reset sort to default when changing event
            setSortModel([{ field: 'firstName', sort: 'asc' }]);
            // Wait for state to update before fetching
            setTimeout(() => {
                if (selectedEvent?.eventId) {
                    fetchPlayers('', paginationModel.pageSize, 0);
                }
            }, 0);
        } catch (error) {
            console.log(error);
        }
    };

    // ✅ Updated useEffect with proper dependency checks
    useEffect(() => {
        if (selectedClub?.clubId && currentEvent?.eventId) {
            const filterValue = filterModel?.quickFilterValues?.join(',') || '';
            
            let sortParams = {};
            if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'firstName': 'firstName',
                    'lastName': 'lastName',
                    'email': 'email',
                    'clubName': 'clubName',
                    'mobileNumber': 'mobileNumber'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
            fetchPlayers(filterValue, paginationModel.pageSize, paginationModel.page, sortParams);
        }
    }, [selectedClub?.clubId, currentEvent?.eventId, paginationModel.page, paginationModel.pageSize]);

    const downloadRosterReport = async () => {
        try {
            startLoading('loading4');
            const response = await ReportingAPI.getRosterRegisteredPlayerReport(currentEvent?.eventId, selectedClub.clubId);
            const url = window.URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'roster_player_report.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.log(error);
        } finally {
            finishLoading('loading4')
        }
    };

    // ✅ Updated columns
    const columns = useMemo(() => [
        {
            field: 'profileLogo',
            headerName: 'Profile',
            flex: 0.5,
            minWidth: 70,
            maxWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.row.firstName || ''} arrow>
                    <img
                        loading="lazy"
                        style={{ height: '40px', width: '40px', borderRadius: '50%', objectFit: 'cover' }}
                        src={params.value ? import.meta.env.VITE_APP_BASE_URL + '/user/user-profile-img/' + params.value : defaultProfileImg}
                        alt="profile"
                    />
                </Tooltip>
            )
        },
        {
            field: 'firstName',
            headerName: 'First Name',
            flex: 1.2,
            minWidth: 140,
            maxWidth: 220,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || ''}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            flex: 1.2,
            minWidth: 140,
            maxWidth: 220,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || ''}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1.2,
            minWidth: 180,
            maxWidth: 300,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => params?.value || 'N/A',
            renderCell: (params) => (
                <Tooltip title={params.value || 'N/A'} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || 'N/A'}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'clubName',
            headerName: 'Club Name',
            flex: 1,
            minWidth: 150,
            maxWidth: 250,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => params?.value || 'N/A',
            renderCell: (params) => (
                <Tooltip title={params.value || 'N/A'} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || 'N/A'}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'mobileNumber',
            headerName: 'Mobile Number',
            flex: 0.8,
            minWidth: 130,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => params?.value || 'N/A',
            renderCell: (params) => (
                <Tooltip title={params.value || 'N/A'} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || 'N/A'}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'actions',
            headerName: 'Action',
            flex: 0.6,
            minWidth: 100,
            maxWidth: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row;
                const guardianIds = row.guardianIds || [];
                const isAuthorized = guardianIds.includes(userInfo.userId) ||
                    row.playerId === userInfo?.userId ||
                    isClubOwner(userInfo) ||
                    isClubStaff(userInfo);

                return (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Tooltip title="View Profile" arrow>
                            <FaRegEye
                                style={{
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={() => {
                                    navigator('/player-profile', {
                                        state: {
                                            player: players.find(p => p.playerId === row.playerId),
                                            event: currentEvent,
                                            tab: 0
                                        }
                                    })
                                }}
                                onMouseEnter={(e) => e.target.style.color = 'var(--hoverColorBold)'}
                                onMouseLeave={(e) => e.target.style.color = '#666'}
                            />
                        </Tooltip>
                        {(isClubOwner(userInfo) || isClubStaff(userInfo)) && (
                            <Tooltip title="Delete Registration" arrow>
                                <AiFillDelete
                                    style={{
                                        fontSize: '20px',
                                        cursor: 'pointer',
                                        color: '#666',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                        setOpenAlert({ isOpen: true, player: players.find(p => p.playerId === row.playerId) })
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#c62828'}
                                    onMouseLeave={(e) => e.target.style.color = '#666'}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            }
        }
    ], [userInfo, players, currentEvent, navigator]);

    const rowsWithId = useMemo(() => {
        return players.map((row, index) => ({
            ...row,
            id: row.playerId || `row-${index}`
        }));
    }, [players]);

    const exportDisabled = !privileges?.MANAGE_ROSTER?.includes(Privileges.DOWNLOAD);

    if (loadingState.loading1 || loadingState.loading2 || loadingState.loading3) {
        return <Loader />
    }

    // ✅ Show message when no events exist
    const hasNoEvents = events.length === 0 && !loadingState.loading2;

    if (hasNoEvents) {
        return (
            <div className='roster_main_conatiner'>
                <div className='roster_player_list_con'>
                    <div className='roster_add_player_btn'>
                        <h1>Roster</h1>
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '400px',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        <p style={{ fontSize: '18px', color: '#666' }}>
                            No events found for this club.
                        </p>
                        <p style={{ fontSize: '14px', color: '#999' }}>
                            Please create an event first to view the roster.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='roster_main_conatiner'>
            {loadingState.loading4 && <Loader />}
            <div className='roster_player_list_con'>
                <div className='roster_add_player_btn'>
                    <h1>Roster</h1>
                    <div className='roster_add_player_btn_option'>
                        <FormControl data-testid="game-dropdown"
                            className='sm_roster_select_season_input'
                            fullWidth>
                            <InputLabel sx={{ top: currentEvent?.eventId ? '0px' : '-8px' }} id="demo-simple-select-autowidth-label">Event</InputLabel>
                            <Select
                                sx={{ width: '200px' }}
                                labelId="demo-simple-select-autowidth-label"
                                id="demo-simple-select-autowidth"
                                value={currentEvent?.eventId || ''}
                                onChange={handleEventChange}
                                name='relation'
                                label="Event"
                                size='small'
                            >
                                {events?.map((item, index) => (
                                    <MenuItem key={index} value={item.eventId}>
                                        {item.eventName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                </div>

                {/* ✅ DataGrid with sorting, filtering, and column resizing */}
                <div style={{ height: 850, width: '100%', marginTop: '16px' }}>
                    <DataGrid
                        rows={rowsWithId}
                        columns={columns}
                        pagination
                        paginationMode="server"
                        rowCount={totalPlayers}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(newModel) => {
                            setPaginationModel(newModel);
                        }}
                        pageSizeOptions={[50, 100]}
                        loading={loadingState.loading4}
                        disableSelectionOnClick
                        getRowId={(row) => row.id}
                        
                        // ✅ Sorting
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        
                        // ✅ Filter handling
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterChange}
                        
                        // ✅ Ensure column resizing is enabled
                        disableColumnResize={false}
                        
                        slots={{
                            toolbar: () => (
                                <CustomToolbar
                                    buttonRef={null}
                                    onExportClick={downloadRosterReport}
                                    quickFilterProps={{
                                        debounceMs: 400,
                                    }}
                                    exportDisabled={exportDisabled}
                                />
                            ),
                        }}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f5f5f5',
                                borderBottom: '2px solid #e8e8e8',
                                fontWeight: 600,
                                color: '#333',
                                fontSize: '14px',
                                textTransform: 'uppercase',
                            },
                            '& .MuiDataGrid-columnSeparator': {
                                visibility: 'visible',
                                color: '#d1d1d1',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                                padding: '8px 16px',
                                fontSize: '14px',
                                color: '#333',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#f5f9ff',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid #e8e8e8',
                                padding: '8px 16px',
                                backgroundColor: '#fafafa',
                            },
                            border: 'none',
                            borderRadius: '8px',
                            '& .MuiDataGrid-toolbarContainer': {
                                padding: 0,
                            },
                            '& .MuiDataGrid-menuIcon': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader:focus': {
                                outline: 'none',
                            },
                        }}
                    />
                </div>
            </div>

            <CustomAlertDialog
                title='Delete Registration'
                submitBtnName='Refund'
                cancelBtnName='Delete'
                isOpen={openAlert.isOpen}
                onCancel={handleDeleteEventPlayerRegistration}
                onClose={() => setOpenAlert({ isOpen: false, player: {} })}
                message={
                    <>
                        Have you refunded <strong><em>{openAlert.player?.firstName} {openAlert.player?.lastName}</em></strong>'s money (if applicable) before deleting?
                        Once deleted, this process cannot be undone, and any pending refunds cannot be processed later.
                    </>
                }
                onSubmit={() => {
                    navigator('/player-profile',
                        {
                            state: {
                                player: openAlert.player,
                                event: currentEvent,
                                tab: 2
                            }
                        }
                    )
                }}
            />
        </div>
    )
}

export default Roster;